import {
  BoundForm,
  boundTextAreaField,
  boundTextField,
  Button,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  StaticField,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import omit from "lodash/omit";
import { Observer } from "mobx-react";
import { InputMaybe, MaterialType } from "src/generated/graphqlTypes";
import { VariantImageAsset } from "src/routes/libraries/material-catalog/components/material-super-drawer/MaterialSuperDrawerContext";
import { MaterialImageEditor } from "src/routes/libraries/material-catalog/components/MaterialImageEditor";
import { useSaveMaterialBrandMutation } from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { queriesResult } from "src/utils";
import { SwapProductFormSwap } from "../swapProductWorkflow";
import {
  DefineReplacementMaterialVariantQuery,
  DefineReplacementMetadataDocument,
  DefineReplacementMetadataQuery,
  useDefineReplacement_SaveMaterialListingMutation,
  useDefineReplacementMaterialVariantQuery,
  useDefineReplacementMetadataQuery,
} from "./DefineReplacementModal.generated";

type DefineReplacementModalProps = {
  formState: ObjectState<SwapProductFormSwap>;
  onSave: (replacement: SwapProductFormSwap) => void;
};

export function DefineReplacementModal(props: DefineReplacementModalProps) {
  const { formState, onSave } = props;
  const projectItemId = formState.originalProjectItemId.value!;
  const materialVariantId = formState.proposedMaterialVariantId.value!;
  const metadataQuery = useDefineReplacementMetadataQuery({ variables: { projectItemId } });
  const maybeMaterialVariantQuery = useDefineReplacementMaterialVariantQuery({
    variables: { materialVariantId: materialVariantId! },
    skip: !materialVariantId, // skip on create mode
  });
  return queriesResult([metadataQuery, maybeMaterialVariantQuery] as const, {
    data: ({ projectItem, materialBrands }, materialVariantData) => (
      <DefineReplacementModalView
        projectItem={projectItem}
        materialBrands={materialBrands}
        materialVariant={materialVariantData?.materialVariant}
        onSave={onSave}
      />
    ),
  });
}

type DefineReplacementModalViewProps = Pick<DefineReplacementModalProps, "onSave"> & {
  projectItem: DefineReplacementMetadataQuery["projectItem"];
  materialBrands: DefineReplacementMetadataQuery["materialBrands"];
  materialVariant?: DefineReplacementMaterialVariantQuery["materialVariant"];
};

function DefineReplacementModalView(props: DefineReplacementModalViewProps) {
  const { projectItem, materialBrands, materialVariant, onSave } = props;
  const { closeModal } = useModal();
  const [saveMaterialListing] = useDefineReplacement_SaveMaterialListingMutation();
  const [saveMaterialBrand] = useSaveMaterialBrandMutation();

  const formState = useFormState({
    config: formConfig,
    init: {
      input: materialVariant,
      map: (mv) => ({
        listingId: mv.listing.id,
        variantId: mv.id,
        name: mv.listing.name,
        brandId: mv.listing.brand?.id,
        modelNumber: mv.modelNumber,
        manufacturerUrl: mv.manufacturerUrl,
        description: mv.listing.description,
        images: mv.images.map(({ id, sortOrder, asset }) => ({
          id,
          sortOrder,
          asset,
          attachmentUrl: asset?.attachmentUrl,
          downloadUrl: asset?.downloadUrl,
        })),
      }),
      ifUndefined: {},
    },
  });

  const brandId = useComputed(() => formState.brandId.value, [formState]);

  async function saveBrand(newBrand: string | undefined) {
    if (newBrand) {
      const { data } = await saveMaterialBrand({
        variables: { input: { name: newBrand } },
        refetchQueries: [DefineReplacementMetadataDocument],
      });
      if (data) formState.brandId.set(data.saveMaterialBrand.materialBrand.id);
    }
  }

  async function onSubmit() {
    const { listingId, variantId, name, brandId, modelNumber, manufacturerUrl, description, images } = formState.value;
    const { data } = await saveMaterialListing({
      variables: {
        input: {
          id: listingId ?? undefined,
          type: MaterialType.Product,
          // A replacement has to fill the original product's slot, so it inherits its Item.
          itemId: projectItem.item.id,
          name,
          brandId,
          description,
          variants: [
            {
              id: variantId ?? undefined,
              modelNumber,
              manufacturerUrl,
              // `downloadUrl`/`attachmentUrl` are FE-only, and images are exhaustive per variant on
              // the backend, so always send the full list from `formState.value`.
              images: images?.map((i) => omit(i, "downloadUrl", "attachmentUrl")),
            },
          ],
        },
      },
    });
    // On create the listing comes back with its single new variant; in edit mode we already have it.
    const materialVariantId = variantId ?? data?.saveMaterialListing.materialVariants.first?.id;
    if (!materialVariantId) return;
    onSave({ proposedMaterialVariantWasCreated: true, proposedMaterialVariantId: materialVariantId });
    closeModal();
  }

  return (
    <>
      <ModalHeader>Define a Custom Product for Replacement</ModalHeader>
      <ModalBody>
        <p css={Css.sm.gray700.mb3.$}>Enter the details for the custom product to replace the selected item.</p>
        <BoundForm
          formState={formState}
          rows={[
            { name: boundTextField({ label: "Product Name" }) },
            {
              reactNodeItem: <StaticField value={projectItem.item.name} label="Item" />,
              reactNodeUnitOfMeasure: <StaticField value={projectItem.unitOfMeasure?.name} label="Unit of Measure" />,
            },
            {
              reactNodeBrandId: (
                <SelectField
                  label="Brand"
                  value={brandId}
                  options={materialBrands ?? []}
                  onSelect={(brandId) => formState.brandId.set(brandId)}
                  onAddNew={saveBrand}
                />
              ),
            },
            { modelNumber: boundTextField({ label: "SKU / Model" }) },
            { manufacturerUrl: boundTextField({ label: "Website" }) },
            { description: boundTextAreaField({ label: "Description" }) },
            { reactNodeImages: <MaterialImageEditor variant={formState} /> },
          ]}
        />
      </ModalBody>
      <ModalFooter>
        <Observer>
          {() => (
            <>
              <Button label="Cancel" variant="quaternary" onClick={closeModal} />
              <Button label={materialVariant ? "Save" : "Add"} disabled={!formState.valid} onClick={onSubmit} />
            </>
          )}
        </Observer>
      </ModalFooter>
    </>
  );
}

type DefineReplacementFormValue = {
  /** Set in edit mode, so the save updates the listing/variant instead of creating another one. */
  listingId?: InputMaybe<string>;
  variantId?: InputMaybe<string>;
  name?: InputMaybe<string>;
  brandId?: InputMaybe<string>;
  modelNumber?: InputMaybe<string>;
  manufacturerUrl?: InputMaybe<string>;
  description?: InputMaybe<string>;
  images?: InputMaybe<Array<VariantImageAsset>>;
};

// Only the name is required, mirroring `materialListingFormConfig`, which leaves the rest of the listing fields unvalidated.
const formConfig: ObjectConfig<DefineReplacementFormValue> = {
  listingId: { type: "value" },
  variantId: { type: "value" },
  name: { type: "value", rules: [required] },
  brandId: { type: "value" },
  modelNumber: { type: "value" },
  manufacturerUrl: { type: "value" },
  description: { type: "value" },
  images: {
    type: "list",
    config: {
      id: { type: "value" },
      sortOrder: { type: "value" },
      attachmentUrl: { type: "value" },
      downloadUrl: { type: "value" },
      asset: {
        type: "object",
        config: {
          id: { type: "value" },
          s3Key: { type: "value" },
          fileName: { type: "value" },
          contentType: { type: "value" },
          sizeInBytes: { type: "value" },
          delete: { type: "value" },
        },
      },
    },
  },
};
